import { FC } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Box, Colors, Typography, useThemeValue } from '@altermaind/web/ui';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';

type Props = {
  data: { name: string; value: number }[];
  colors?: (keyof Colors)[];
  centerLabelValue?: string;
  centerValue?: string | number;
};

export const HalfPieChartComponent: FC<Props> = ({
  data,
  colors = ['primary', 'white'],
  centerLabelValue,
  centerValue,
}) => {
  const total = data.reduce((sum, entry) => sum + entry.value, 0);

  const primaryColor = useThemeValue('colors', colors[0]);
  const secondaryColor = useThemeValue('colors', colors[1]);

  const backgroundData = [{ name: 'total', value: total }];

  const foregroundData = [
    { name: data[0].name, value: data[0].value },
    { name: data[1].name, value: total - data[0].value },
  ];

  const { genericResponsiveValue } = useResponsiveStyle();

  return (
    <Box position='relative'>
      <ResponsiveContainer width='100%' height={150}>
        <PieChart>
          <Pie
            data={backgroundData}
            cx='50%'
            cy='100%'
            startAngle={180}
            endAngle={0}
            innerRadius={90}
            outerRadius={110}
            dataKey='value'
            cornerRadius={20}
            stroke='none'
          >
            <Cell fill={secondaryColor} />
          </Pie>

          <Pie
            data={foregroundData}
            cx='50%'
            cy='100%'
            startAngle={180}
            endAngle={0}
            innerRadius={90}
            outerRadius={110}
            dataKey='value'
            cornerRadius={20}
            stroke='none'
          >
            <Cell fill={primaryColor} />
            <Cell fill='transparent' />
          </Pie>

          <Tooltip
            formatter={(value, name) => [`${value}`, name]}
            contentStyle={{
              backgroundColor: '#fff',
              color: '#000',
              borderRadius: '8px',
              padding: '6px 10px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
              fontSize: '14px',
              border: 'none',
              zIndex: 1000,
              position: 'relative',
            }}
          />
        </PieChart>
      </ResponsiveContainer>

      {centerLabelValue && centerValue && (
        <Box
          position='absolute'
          top='60%'
          left='50%'
          style={{ transform: 'translate(-50%, -50%)' }}
          textAlign='center'
          zIndex={0}
        >
          <Box textAlign='center' maxWidth='80%' mx='auto'>
            <Typography variant='special' color='white'>
              {centerValue}
            </Typography>
            <Typography variant='p2/light' color='white'>
              {centerLabelValue}
            </Typography>
          </Box>
        </Box>
      )}

      <Box
        justifyContent='center'
        width='100%'
        display='flex'
        flexDirection={genericResponsiveValue({
          breakPoints: { desktop: 'row' },
          defaultValue: 'column',
        })}
        mt={centerLabelValue && centerValue ? 'xl' : 's'}
      >
        {data.map((entry, index) => (
          <Box alignContent='center' key={entry.name} display='flex' alignItems='center' mx='m'>
            <Box
              width='xxs'
              height='xxs'
              backgroundColor={colors[index % colors.length]}
              borderRadius='50%'
              mr='xxxs'
            />
            <Typography variant='p2/light' color='white'>
              {entry.name}: {entry.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
